import React from 'react';
import PropTypes from 'prop-types';
import getConfig from 'next/config';
import ReactJWPlayer from 'react-jw-player';

import Play from '~/src/components/svg/general/Play';
import Caption from '~/src/components/caption/Caption';
import EmbedIframe from '~/src/components/embed/iframe/EmbedIframe';

import {sendPixel} from '~/src/utilities/tracking/netmetrix';
import {WemfTypes} from '~/src/utilities/constants/tracking';
import {getVastTag} from '~/src/utilities/libs/totm';
import {trackEvent, triggerEvent, isEventRegistered, deleteEvent} from '~/src/utilities/tracking/beagle';
import {BeagleEvents, GtmEvents} from '~/src/utilities/constants/tracking';
import {trackGtm} from '~/src/utilities/tracking/gtm';
import {withSettingsContext} from '~/src/components/context/SettingsContext';
import {isLegacyAppSession} from '~/src/utilities/helpers/client';
import {classNames} from '~/src/utilities/helpers/css';

import css from './VideoPlayer.module.scss';

const {publicRuntimeConfig: config} = getConfig();

class VideoPlayer extends React.Component {
    onTenPercentEventIsFired = false;

    onAdPlay = () => {
        trackGtm(GtmEvents.VIDEO_AD_START, this.props);
        this.props.events.onAdPlay && this.props.events.onAdPlay();
    };

    onAdComplete = () => {
        trackGtm(GtmEvents.VIDEO_AD_COMPLETE, this.props);
        this.props.events.onAdComplete && this.props.events.onAdComplete();
    };

    trackOnPlay = ({autoStart}) => {
        if (!autoStart) {
            sendPixel(WemfTypes.VIDEO);
        }
        if (isEventRegistered(BeagleEvents.OPEN_VIDEO)) {
            deleteEvent(BeagleEvents.OPEN_VIDEO, true);
            triggerEvent(BeagleEvents.OPEN_VIDEO, {
                resourceId: this.props.video_id
            });
        } else {
            trackEvent(BeagleEvents.OPEN_VIDEO, {resourcePositionPage: this.props.storyId || 'videoPortal', resourceId: this.props.video_id});
        }
        trackEvent(BeagleEvents.VIDEO_PERCENTAGE_WATCHED, {resourceId: this.props.video_id, value: 0});
        trackGtm(GtmEvents.VIDEO_PLAY, this.props);
    };

    onPlay = () => {
        if (this.onPlayEventReady.current) {
            this.trackOnPlay({autoStart: false});
            this.props.events.onPlay && this.props.events.onPlay();
        } else {
            this.onPlayEventReady.current = true;
        }
    };

    onAutoStart = () => {
        this.trackOnPlay({autoStart: true});
        this.props.events.onAutoStart && this.props.events.onAutoStart();
    };

    onResume = () => {
        sendPixel(WemfTypes.VIDEO);
        trackGtm(GtmEvents.VIDEO_RESUME, this.props);
        this.props.events.onResume && this.props.events.onResume();
    };

    onSeek = (event) => {
        const progress = Math.round((event.offset / window.jwplayer(this.props.video_id).getDuration()) * 100);
        trackGtm(GtmEvents.VIDEO_SEEK, {...this.props, progress: `${progress}%`, progressMethod: 'seek'});
        this.props.events.onSeek && this.props.events.onSeek();
    };

    onTenPercent = () => {
        trackGtm(GtmEvents.VIDEO_PROGRESS, {...this.props, progress: '10%', progressMethod: 'watch'});
        this.props.events.onTenPercent && this.props.events.onTenPercent();
    };

    onTwentyFivePercent = () => {
        trackEvent(BeagleEvents.VIDEO_PERCENTAGE_WATCHED, {resourceId: this.props.video_id, value: 0.25});
        trackGtm(GtmEvents.VIDEO_PROGRESS, {...this.props, progress: '25%', progressMethod: 'watch'});
        this.props.events.onTwentyFivePercent && this.props.events.onTwentyFivePercent();
    };

    onFiftyPercent = () => {
        trackEvent(BeagleEvents.VIDEO_PERCENTAGE_WATCHED, {resourceId: this.props.video_id, value: 0.5});
        trackGtm(GtmEvents.VIDEO_PROGRESS, {...this.props, progress: '50%', progressMethod: 'watch'});
        this.props.events.onFiftyPercent && this.props.events.onFiftyPercent();
    };

    onSeventyFivePercent = () => {
        trackEvent(BeagleEvents.VIDEO_PERCENTAGE_WATCHED, {resourceId: this.props.video_id, value: 0.75});
        trackGtm(GtmEvents.VIDEO_PROGRESS, {...this.props, progress: '75%', progressMethod: 'watch'});
        this.props.events.onSeventyFivePercent && this.props.events.onSeventyFivePercent();
    };

    onOneHundredPercent = () => {
        trackEvent(BeagleEvents.VIDEO_PERCENTAGE_WATCHED, {resourceId: this.props.video_id, value: 1});
        trackGtm(GtmEvents.VIDEO_COMPLETE, this.props);
        this.props.events.onOneHundredPercent && this.props.events.onOneHundredPercent();
    };

    onTime = (event) => {
        const posInPercent = Math.round((event.position / event.duration) * 100);
        if (posInPercent >= 10 && !this.onTenPercentEventIsFired) {
            this.onTenPercentEventIsFired = true;
            this.onTenPercent();
        }
    };

    onEnterFullscreen = () => {
        sendPixel(WemfTypes.VIDEO);
        this.props.events.onEnterFullscreen && this.props.events.onEnterFullscreen();
    };

    onThumbnailClick = () => {
        this.setState({showThumbnail: false});
    };

    prepareForApp() {
        if (this.props.context.settings.isWebView || isLegacyAppSession()) {
            this.setState({
                isApp: true,
                showThumbnail: false
            });

            return true;
        }

        return false;
    }

    renderVideo() {
        if (this.state.isApp) {
            return <EmbedIframe url={this.props.fallbackUrl} />;
        }

        return this.state.jwPlayerIsReady && <ReactJWPlayer {...this.jwPlayerConfig} />;
    }

    static defaultProps = {
        events: {}
    };

    constructor(props) {
        super(props);

        const events = {...props.events};
        events.onTime = this.onTime;
        events.onAdPlay = this.onAdPlay;
        events.onAdComplete = this.onAdComplete;
        events.onPlay = this.onPlay;
        events.onAutoStart = this.onAutoStart;
        events.onResume = this.onResume;
        events.onSeek = this.onSeek;
        events.onTenPercent = this.onTenPercent;
        events.onTwentyFivePercent = this.onTwentyFivePercent;
        events.onFiftyPercent = this.onFiftyPercent;
        events.onSeventyFivePercent = this.onSeventyFivePercent;
        events.onOneHundredPercent = this.onOneHundredPercent;
        events.onEnterFullscreen = this.onEnterFullscreen;

        this.jwPlayerConfig = {
            aspectRatio: '16:9',
            playerId: props.video_id,
            playerScript: config.scripts.jwplayer,
            file: props.url_high,
            licenseKey: config.videoPlayer.jwplayerKey,
            image: props.thumbnail,
            ...events,
            className: css.videoPlayerContent,
            isAutoPlay: true,
            isMuted: false,
            customProps: {
                width: '100%',
                advertising: this.props.show_ads
                    ? {
                          client: 'googima'
                      }
                    : {}
            }
        };

        this.state = {
            showThumbnail: !props.autoplay,
            jwPlayerIsReady: !props.show_ads,
            isApp: false
        };
        this.onPlayEventReady = React.createRef();
        this.onPlayEventReady.current = !props.autoplay;
    }

    componentDidMount() {
        if (!this.prepareForApp() && this.props.show_ads) {
            getVastTag({
                isWebview: this.props.context.settings.isWebview,
                storyid: this.props.metadataid,
                videoid: this.props.video_id,
                duration: this.props.duration,
                geoblocking: this.props.geoblocking,
                aspectratio: this.props.aspect_ratio,
                content_category: this.props.meta.content_category
            }).then((vastTag) => {
                this.jwPlayerConfig.generatePrerollUrl = () => vastTag;
                this.setState({
                    jwPlayerIsReady: true
                });
            });
        }
    }

    render() {
        const style = {
            backgroundImage: `url(${this.props.thumbnail})`
        };

        if (this.state.showThumbnail) {
            return (
                <Caption {...this.props}>
                    <div className={css.videoPlayerWrapper} style={style} onClick={this.onThumbnailClick}>
                        <Play />
                    </div>
                </Caption>
            );
        }

        const {isApp} = this.state;
        const className = classNames(css, 'videoPlayerWrapper', {isApp});

        return (
            <div className={className} style={style}>
                {this.renderVideo()}
            </div>
        );
    }
}

if (process.env.NODE_ENV === 'development') {
    VideoPlayer.propTypes = {
        video_id: PropTypes.string.isRequired,
        url_high: PropTypes.string.isRequired,
        thumbnail: PropTypes.string,
        autoplay: PropTypes.bool,
        fallbackUrl: PropTypes.string,
        events: function (props, propName, componentName) {
            const prop = props[propName];
            if (prop && Object.keys(prop).some((key) => !key.startsWith('on'))) {
                return new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`, expected valid event names only.`);
            }
        }
    };
}

export {VideoPlayer};

export default withSettingsContext(VideoPlayer);
